/**
 * PWA Service - Manages service worker registration and offline functionality
 */

interface PWAInstallPrompt {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PWAService {
  isSupported: boolean;
  isInstalled: boolean;
  isOffline: boolean;
  installPrompt: PWAInstallPrompt | null;
  registration: ServiceWorkerRegistration | null;
}

class PWAManager {
  private static instance: PWAManager;
  private installPrompt: PWAInstallPrompt | null = null;
  private registration: ServiceWorkerRegistration | null = null;
  private callbacks: {
    onInstallPrompt: ((prompt: PWAInstallPrompt) => void)[];
    onInstalled: (() => void)[];
    onOffline: (() => void)[];
    onOnline: (() => void)[];
    onUpdate: ((registration: ServiceWorkerRegistration) => void)[];
  } = {
    onInstallPrompt: [],
    onInstalled: [],
    onOffline: [],
    onOnline: [],
    onUpdate: []
  };

  private constructor() {
    this.setupEventListeners();
  }

  static getInstance(): PWAManager {
    if (!PWAManager.instance) {
      PWAManager.instance = new PWAManager();
    }
    return PWAManager.instance;
  }

  private setupEventListeners(): void {
    // Listen for install prompt
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.installPrompt = e as any;
      this.callbacks.onInstallPrompt.forEach(callback => callback(this.installPrompt!));
    });

    // Listen for app installed
    window.addEventListener('appinstalled', () => {
      this.installPrompt = null;
      this.callbacks.onInstalled.forEach(callback => callback());
    });

    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.callbacks.onOnline.forEach(callback => callback());
    });

    window.addEventListener('offline', () => {
      this.callbacks.onOffline.forEach(callback => callback());
    });
  }

  async registerServiceWorker(): Promise<void> {
    if (!this.isServiceWorkerSupported()) {
      console.warn('Service workers not supported');
      return;
    }

    try {
      this.registration = await navigator.serviceWorker.register('/sw.js');
      
      console.log('Service worker registered:', this.registration);

      // Listen for updates
      this.registration.addEventListener('updatefound', () => {
        const newWorker = this.registration!.installing;
        
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New service worker is available
              this.callbacks.onUpdate.forEach(callback => callback(this.registration!));
            }
          });
        }
      });

      // Check for updates periodically
      setInterval(() => {
        this.registration?.update();
      }, 60000); // Check every minute

    } catch (error) {
      console.error('Service worker registration failed:', error);
    }
  }

  async promptInstall(): Promise<boolean> {
    if (!this.installPrompt) {
      return false;
    }

    try {
      await this.installPrompt.prompt();
      const { outcome } = await this.installPrompt.userChoice;
      
      if (outcome === 'accepted') {
        this.installPrompt = null;
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Install prompt failed:', error);
      return false;
    }
  }

  async updateServiceWorker(): Promise<void> {
    if (!this.registration) {
      return;
    }

    const newWorker = this.registration.waiting;
    if (newWorker) {
      newWorker.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  }

  isServiceWorkerSupported(): boolean {
    return 'serviceWorker' in navigator;
  }

  isInstallable(): boolean {
    return this.installPrompt !== null;
  }

  isInstalled(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches ||
           (window.navigator as any).standalone === true;
  }

  isOffline(): boolean {
    return !navigator.onLine;
  }

  getStatus(): PWAService {
    return {
      isSupported: this.isServiceWorkerSupported(),
      isInstalled: this.isInstalled(),
      isOffline: this.isOffline(),
      installPrompt: this.installPrompt,
      registration: this.registration
    };
  }

  // Event listeners
  onInstallPrompt(callback: (prompt: PWAInstallPrompt) => void): void {
    this.callbacks.onInstallPrompt.push(callback);
  }

  onInstalled(callback: () => void): void {
    this.callbacks.onInstalled.push(callback);
  }

  onOffline(callback: () => void): void {
    this.callbacks.onOffline.push(callback);
  }

  onOnline(callback: () => void): void {
    this.callbacks.onOnline.push(callback);
  }

  onUpdate(callback: (registration: ServiceWorkerRegistration) => void): void {
    this.callbacks.onUpdate.push(callback);
  }

  // Offline storage helpers
  async cacheImportantData(): Promise<void> {
    if (!this.registration) {
      return;
    }

    try {
      const cache = await caches.open('sprout-important-data');
      const importantUrls = [
        '/api/users/me',
        '/api/children?limit=50',
        '/api/classrooms',
        '/api/observations?limit=20'
      ];

      for (const url of importantUrls) {
        try {
          const response = await fetch(url);
          if (response.ok) {
            await cache.put(url, response.clone());
          }
        } catch (error) {
          console.warn(`Failed to cache ${url}:`, error);
        }
      }
    } catch (error) {
      console.error('Failed to cache important data:', error);
    }
  }

  async clearCache(): Promise<void> {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
    } catch (error) {
      console.error('Failed to clear cache:', error);
    }
  }

  // Background sync helpers
  async scheduleBackgroundSync(tag: string): Promise<void> {
    if (!this.registration) {
      return;
    }

    try {
      // Background sync is not yet widely supported, check if available
      if ('sync' in this.registration) {
        await (this.registration as any).sync.register(tag);
      } else {
        console.warn('Background sync not supported in this browser');
      }
    } catch (error) {
      console.error('Background sync registration failed:', error);
    }
  }

  // Push notifications (if implemented)
  async requestNotificationPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }

    return false;
  }

  async subscribeToPushNotifications(): Promise<PushSubscription | null> {
    if (!this.registration) {
      return null;
    }

    try {
      const subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: process.env.REACT_APP_VAPID_PUBLIC_KEY
      });

      // Send subscription to server
      await fetch('/api/notifications/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(subscription)
      });

      return subscription;
    } catch (error) {
      console.error('Push subscription failed:', error);
      return null;
    }
  }
}

export default PWAManager.getInstance();
